import { useState, useEffect, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Target, Search, Loader2, Award, CheckCircle2, XCircle, MinusCircle, Printer, Ban, ShieldCheck } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { usePlatform, UNIFIED, DEFAULT_PLATFORM } from "../lib/ridersPlatform";
import { RiderPlatformSwitcher } from "./RiderPlatformSwitcher";

const AR_MONTHS = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];
const monthLabel = (ym) => {
  const [y, m] = String(ym).split("-");
  return `${AR_MONTHS[parseInt(m, 10) - 1] || m} ${y}`;
};
const monthBounds = (ym) => {
  if (!ym) return [null, null];
  const [y, m] = ym.split("-").map(Number);
  const last = new Date(y, m, 0).getDate();
  return [`${ym}-01`, `${ym}-${String(last).padStart(2, "0")}`];
};
const riderName = (r) => r.emp_name || r.rider_name || r.rider_id;

// Bronze = met (>=98%), Green = close (95-97%), Red = below (<95%)
const tierOf = (comp, target) => {
  const tt = Number(target) || 0;
  if (tt <= 0) return null;
  const pct = (Number(comp) || 0) / tt;
  if (pct >= 0.98) return "met";
  if (pct >= 0.95) return "near";
  return "below";
};
const BADGE_CLS = {
  met: "bg-[#f3e4cf] text-[#8a541b] border-[#d8b483]",
  near: "bg-emerald-100 text-emerald-700 border-emerald-200",
  below: "bg-red-100 text-red-700 border-red-200",
};
const ROW_CLS = { met: "bg-[#faf3e8]", near: "bg-emerald-50/40", below: "bg-red-50/40" };

const StatCard = ({ icon: Icon, label, value, cls, onClick, active, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid || "target-stat-card"}
    className={`flex items-center gap-3 rounded-2xl border p-4 text-right w-full transition hover:shadow-md cursor-pointer ${cls} ${active ? "ring-2 ring-offset-1 ring-slate-500/60 shadow-md" : ""}`}>
    <Icon className="h-7 w-7 shrink-0" />
    <div>
      <div className="font-heading text-2xl font-extrabold font-mono-nums leading-none">{value}</div>
      <div className="text-xs font-semibold opacity-80 mt-1">{label}</div>
    </div>
  </button>
);

export const RidersTargetPanel = () => {
  const { t } = useLang();
  const [platform] = usePlatform();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState("");
  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState(null);

  const [effFrom, effTo] = month ? monthBounds(month) : [null, null];
  const plat = platform === UNIFIED ? DEFAULT_PLATFORM : platform;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.ridersOverview("cumulative", null, "all", "all", effFrom, effTo, plat, "month");
      setData(res);
    } catch { toast.error(t("dash.riders.loadFail")); }
    finally { setLoading(false); }
  }, [plat, effFrom, effTo, t]);

  useEffect(() => { load(); }, [load]);

  const months = useMemo(() => {
    const set = new Set((data?.available_dates || []).map((d) => d.slice(0, 7)));
    return [...set].sort().reverse();
  }, [data]);

  useEffect(() => {
    if (!month && months.length) setMonth(months[0]);
  }, [months, month]);

  const rows = useMemo(() => {
    const list = [...(data?.rows || [])];
    const TIER_RANK = { met: 0, near: 1, below: 2 };
    list.sort((a, b) => {
      const ta = TIER_RANK[tierOf(a.completed_deliveries, a.order_target)] ?? 3;
      const tb = TIER_RANK[tierOf(b.completed_deliveries, b.order_target)] ?? 3;
      if (ta !== tb) return ta - tb;
      return (b.completed_deliveries || 0) - (a.completed_deliveries || 0);
    });
    return list;
  }, [data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = rows;
    if (tierFilter) {
      list = list.filter((r) => (tierOf(r.completed_deliveries, r.order_target) || "none") === tierFilter);
    }
    if (q) list = list.filter((r) => String(riderName(r)).toLowerCase().includes(q) || String(r.rider_id).includes(q));
    return list;
  }, [rows, search, tierFilter]);

  const stats = useMemo(() => {
    let met = 0, near = 0, below = 0, none = 0;
    for (const r of rows) {
      const tier = tierOf(r.completed_deliveries, r.order_target);
      if (tier === "met") met++;
      else if (tier === "near") near++;
      else if (tier === "below") below++;
      else none++;
    }
    return { met, near, below, none };
  }, [rows]);

  const hasTarget = rows.some((r) => (Number(r.order_target) || 0) > 0);

  const clearSuspension = async (empId, name) => {
    if (!empId) return;
    if (!window.confirm(`رفع الإيقاف وتصفير عدّاد الإنذارات للمندوب «${name}»؟`)) return;
    try { await api.clearTargetSuspension(empId); toast.success("تم رفع الإيقاف وتصفير العدّاد"); load(); }
    catch { toast.error("تعذّر رفع الإيقاف"); }
  };

  const printPdf = () => {
    if (!filtered.length) { toast.warning(t("dash.riders.tgt.noData")); return; }
    const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const STATUS = { met: t("dash.riders.tgt.met"), near: t("dash.riders.tgt.nearLabel"), below: t("dash.riders.tgt.notMet") };
    const PDF_CLR = { met: "background:#f3e4cf;color:#8a541b;", near: "background:#d1fae5;color:#047857;", below: "background:#fee2e2;color:#b91c1c;" };
    const body = filtered.map((r, i) => {
      const tgt = Number(r.order_target) || 0;
      const comp = Number(r.completed_deliveries) || 0;
      const tier = tierOf(comp, tgt);
      const rem = tgt > 0 && comp < tgt ? tgt - comp : 0;
      const status = tier ? `<span style="padding:1px 6px;border-radius:5px;${PDF_CLR[tier]}">${esc(STATUS[tier])}</span>` : "—";
      return `<tr><td class="num">${i + 1}</td><td class="rider">${esc(riderName(r))}${(r.emp_name || r.rider_name) ? ` <span class="rid">#${esc(r.rider_id)}</span>` : ""}</td><td class="num"><b>${comp}</b></td><td class="num">${tgt || "—"}</td><td class="num">${tgt ? rem : "—"}</td><td>${status}</td></tr>`;
    }).join("");
    const html = `<!doctype html><html dir="rtl" lang="ar"><head><style>@font-face{font-family:SaudiRiyal;src:url(/fonts/riyal.woff2) format("woff2");unicode-range:U+20C1}</style><meta charset="utf-8"><title>${esc(t("dash.riders.tgt.title"))} — ${esc(plat)}</title>
      <style>
        @page { size: A4 portrait; margin: 12mm; }
        body { font-family:SaudiRiyal,'IBM Plex Sans Arabic','Arial',sans-serif; color:#0f172a; }
        h1 { font-size:16px; margin:0 0 2px; text-align:center; }
        .sub { text-align:center; font-size:11px; color:#475569; margin-bottom:10px; }
        table { width:100%; border-collapse:collapse; font-size:10px; }
        th,td { border:1px solid #cbd5e1; padding:4px 6px; text-align:center; }
        thead th { background:#2563eb; color:#fff; }
        tbody tr:nth-child(even) { background:#f8fafc; }
        td.rider { text-align:right; font-weight:600; }
        td.num { direction:ltr; }
        .rid { color:#94a3b8; font-weight:400; font-size:8px; }
      </style></head><body>
      <h1>${esc(t("dash.riders.tgt.title"))} — ${esc(plat)}</h1>
      <div class="sub">${esc(month ? monthLabel(month) : "")} · ${esc(t("dash.riders.tgt.achieved"))}: ${stats.met} · ${esc(t("dash.riders.tgt.near"))}: ${stats.near} · ${esc(t("dash.riders.tgt.notAchieved"))}: ${stats.below}</div>
      <table><thead><tr>
        <th>${esc(t("dash.riders.tgt.rank"))}</th><th>${esc(t("dash.riders.tgt.rider"))}</th><th>${esc(t("dash.riders.tgt.orders"))}</th><th>${esc(t("dash.riders.tgt.targetVal"))}</th><th>${esc(t("dash.riders.tgt.remaining"))}</th><th>${esc(t("dash.riders.tgt.status"))}</th>
      </tr></thead><tbody>${body}</tbody></table>
      <script>window.onload=function(){window.print();}</script>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("فعّل النوافذ المنبثقة للطباعة"); return; }
    w.document.write(html); w.document.close();
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="riders-target-panel">
      {/* Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-lg shadow-blue-600/20">
            <Target className="h-6 w-6" />
          </div>
          <div>
            <h2 className="font-heading text-lg font-bold text-slate-900">{t("dash.riders.tgt.title")}</h2>
            <p className="text-sm text-slate-500 max-w-2xl">{t("dash.riders.tgt.sub")}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <RiderPlatformSwitcher />
          {months.length > 0 && (
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="w-40" data-testid="target-month-select"><SelectValue placeholder={t("dash.riders.tgt.monthLbl")} /></SelectTrigger>
              <SelectContent>
                {months.map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" size="sm" onClick={printPdf} data-testid="target-pdf-button" className="gap-1.5">
            <Printer className="h-4 w-4" /> <span className="hidden sm:inline">{t("dash.riders.tgt.exportPdf")}</span>
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-slate-500">{t("dash.riders.tgt.noData")}</div>
      ) : (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard icon={Award} value={stats.met} label={t("dash.riders.tgt.achieved")} cls="border-[#d8b483] bg-[#f9f1e4] text-[#8a541b]"
              testid="target-stat-met" active={tierFilter === "met"} onClick={() => setTierFilter((f) => (f === "met" ? null : "met"))} />
            <StatCard icon={CheckCircle2} value={stats.near} label={t("dash.riders.tgt.near")} cls="border-emerald-200 bg-emerald-50 text-emerald-700"
              testid="target-stat-near" active={tierFilter === "near"} onClick={() => setTierFilter((f) => (f === "near" ? null : "near"))} />
            <StatCard icon={XCircle} value={stats.below} label={t("dash.riders.tgt.notAchieved")} cls="border-red-200 bg-red-50 text-red-700"
              testid="target-stat-below" active={tierFilter === "below"} onClick={() => setTierFilter((f) => (f === "below" ? null : "below"))} />
            <StatCard icon={MinusCircle} value={stats.none} label={t("dash.riders.tgt.noTargetRiders")} cls="border-slate-200 bg-slate-50 text-slate-600"
              testid="target-stat-none" active={tierFilter === "none"} onClick={() => setTierFilter((f) => (f === "none" ? null : "none"))} />
          </div>

          {!hasTarget && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="target-no-target-hint">{t("dash.riders.tgt.noTargetSet")}</div>
          )}

          {/* Search */}
          <div className="relative w-full md:w-72">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="target-search-input" placeholder={t("dash.riders.tgt.searchPh")} value={search}
              onChange={(e) => setSearch(e.target.value)} className="pr-9" />
          </div>

          {/* Ranked list */}
          <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto" data-testid="riders-target-table">
            <div className="min-w-[700px]">
              <div className="grid grid-cols-[44px_1fr_78px_78px_88px_104px_96px] bg-slate-50 border-b border-slate-200 text-xs text-slate-600 font-semibold">
                <div className="px-2 py-2.5 text-center">{t("dash.riders.tgt.rank")}</div>
                <div className="px-3 py-2.5 text-right">{t("dash.riders.tgt.rider")}</div>
                <div className="px-2 py-2.5 text-center">{t("dash.riders.tgt.orders")}</div>
                <div className="px-2 py-2.5 text-center">{t("dash.riders.tgt.targetVal")}</div>
                <div className="px-2 py-2.5 text-center">{t("dash.riders.tgt.remaining")}</div>
                <div className="px-2 py-2.5 text-center">{t("dash.riders.tgt.status")}</div>
                <div className="px-2 py-2.5 text-center">إنذارات</div>
              </div>
              {filtered.length === 0 && (
                <div className="px-4 py-10 text-center text-sm text-slate-400" data-testid="target-filter-empty">
                  لا يوجد مندوبون في هذه الفئة
                </div>
              )}
              {filtered.map((r, i) => {
                const tgt = Number(r.order_target) || 0;
                const comp = Number(r.completed_deliveries) || 0;
                const tier = tierOf(comp, tgt);
                const remaining = tgt > 0 && comp < tgt ? tgt - comp : 0;
                const statusLabel = tier === "met" ? t("dash.riders.tgt.met") : tier === "near" ? t("dash.riders.tgt.nearLabel") : t("dash.riders.tgt.notMet");
                const streak = Number(r.fail_streak) || 0;
                return (
                  <div key={r.rider_id} data-testid={`target-row-${r.rider_id}`}
                    className={`grid grid-cols-[44px_1fr_78px_78px_88px_104px_96px] items-center border-b border-slate-100 text-sm ${r.suspended ? "bg-red-50" : tier ? ROW_CLS[tier] : ""}`}>
                    <div className="px-2 py-2.5 text-center">
                      {i === 0 ? <Award className="inline h-4 w-4 text-[#b8860b]" /> : <span className="font-mono-nums text-slate-500">{i + 1}</span>}
                    </div>
                    <div className="px-3 py-2.5 text-right min-w-0">
                      <div className="font-semibold text-slate-800 truncate">{riderName(r)}</div>
                      {(r.emp_name || r.rider_name) && <div className="text-[10px] text-slate-400 font-mono-nums">#{r.rider_id}</div>}
                    </div>
                    <div className="px-2 py-2.5 text-center font-heading font-extrabold font-mono-nums text-slate-900">{comp}</div>
                    <div className="px-2 py-2.5 text-center font-mono-nums text-slate-600">{tgt || "—"}</div>
                    <div className="px-2 py-2.5 text-center font-mono-nums text-slate-600">{tgt ? remaining : "—"}</div>
                    <div className="px-2 py-2.5 text-center">
                      {tier === null ? (
                        <span className="text-slate-300">—</span>
                      ) : (
                        <span className={`inline-flex items-center rounded-lg border px-2 py-0.5 text-xs font-bold ${BADGE_CLS[tier]}`}>
                          {statusLabel}
                        </span>
                      )}
                    </div>
                    <div className="px-2 py-2.5 text-center">
                      {r.suspended ? (
                        <button data-testid={`clear-suspension-${r.rider_id}`} onClick={() => clearSuspension(r.emp_id, riderName(r))}
                          title="موقوف — اضغط لرفع الإيقاف"
                          className="inline-flex items-center gap-1 rounded-lg bg-red-600 text-white px-2 py-1 text-[11px] font-bold hover:bg-red-700">
                          <Ban className="h-3 w-3" /> رفع الإيقاف
                        </button>
                      ) : streak > 0 ? (
                        <span className="inline-flex items-center gap-1 rounded-lg bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 text-xs font-bold font-mono-nums">{streak}</span>
                      ) : (
                        <span className="inline-flex items-center justify-center text-emerald-400"><ShieldCheck className="h-4 w-4" /></span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </motion.div>
  );
};
